/**
 * Build an effect × effect correlation matrix from per-effect response records.
 *
 * Each cell correlates the per-respondent mean scores of two MSI effects,
 * paired via pairRespondentScores (Persona when both sides are persona-linked,
 * FormID otherwise — same-questionnaire pairing).
 * Pure function — no Firebase imports.
 */
import pearsonCorrelation from './correlation'
import pairRespondentScores, { resolvePairKey } from './pairRespondentScores'

/**
 * @typedef {import('./correlation').CorrelationResult} CorrelationResult
 */

/**
 * @typedef {object} CorrelationMatrix
 * @property {Array<{id: string, name: string}>} effects - Matrix axis, in input order
 * @property {Object<string, Object<string, CorrelationResult>>} cells - cells[idA][idB]; symmetric, diagonal r = 1
 */

const toScore = (value) => {
    if (value === '' || value == null) return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

// Unique respondents with at least one numeric score — diagonal n
const countRespondents = (records, key) => {
    const seen = new Set()
    ;(Array.isArray(records) ? records : []).forEach((r) => {
        if (r?.[key] && toScore(r?.Input) !== null) seen.add(r[key])
    })
    return seen.size
}

/**
 * @param {Array<{ID: string, Effect?: string, Name?: string}>} effects - MSI effects (OutputEffects docs)
 * @param {Object<string, object[]>} recordsByEffectId - useHierarchyData records per effect ID
 * @param {object} [options]
 * @param {number} [options.minPairs] - Minimum shared respondents per cell
 * @param {string} [options.anchorId] - Effect ID to move to the front of the axis
 * @returns {CorrelationMatrix}
 */
const buildCorrelationMatrix = (effects, recordsByEffectId, { minPairs, anchorId } = {}) => {
    // OutputEffects docs carry the display name in `Effect`; `Name` is the
    // fallback for callers that pass pre-mapped objects.
    const list = (Array.isArray(effects) ? effects : []).map((e) => ({
        id: e.ID,
        name: e.Effect || e.Name || '',
    }))

    if (anchorId) {
        const anchorIndex = list.findIndex((e) => e.id === anchorId)
        if (anchorIndex > 0) list.unshift(list.splice(anchorIndex, 1)[0])
    }

    /** @type {Object<string, Object<string, CorrelationResult>>} */
    const cells = {}
    list.forEach(({ id }) => {
        cells[id] = {}
    })

    list.forEach(({ id: idA }, i) => {
        const recordsA = recordsByEffectId?.[idA] || []

        // Diagonal: an effect correlates perfectly with itself
        const keyA = resolvePairKey(recordsA, recordsA)
        cells[idA][idA] = { r: 1, n: countRespondents(recordsA, keyA), insufficient: false }

        list.slice(i + 1).forEach(({ id: idB }) => {
            const recordsB = recordsByEffectId?.[idB] || []
            const key = resolvePairKey(recordsA, recordsB)
            const pairs = pairRespondentScores(recordsA, recordsB, { key })
            const result = pearsonCorrelation(
                pairs,
                minPairs != null ? { minPairs } : undefined
            )
            cells[idA][idB] = result
            cells[idB][idA] = result
        })
    })

    return { effects: list, cells }
}

export default buildCorrelationMatrix
